import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "../../utils/toast";
import { useSubmitGuard } from "../../utils/useSubmitGuard";
import { errorMessage } from "../../utils/errorMessage";
import { API_DETAILS } from "../../App";

import './../../App.css';

const HelpSupportView = (props) => {
  const [viewVal, setViewVal] = useState(0);
  const navigate = useNavigate();
  const [isSent, setIsSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [, guard] = useSubmitGuard();
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');

  const isValid = subject.trim().length > 0 && message.trim().length > 0;

  // This used to set isSent and console.log, so the page said "Message Sent!"
  // while nothing was sent to anyone. Confirmation now waits on the server.
  const handleSendMessage = guard(async () => {
    if (sending) return;
    setSending(true);
    try {
      const url =
        API_DETAILS.baseUrl +
        API_DETAILS.PORT +
        API_DETAILS.baseExtension +
        "/support/message";
      await axios.post(url, { subject, message });
      setIsSent(true);
      setSubject('');
      setMessage('');
      setTimeout(() => setIsSent(false), 4000);
    } catch (err) {
      toast.error(
        errorMessage(err, "We couldn't send your message. Try again, or email us at yourohealth1@gmail.com.")
      );
    } finally {
      setSending(false);
    }
  });

  const navToProfile = (viewVal) => props.changeView(viewVal);
  useEffect(() => {
    if (viewVal === 4 || viewVal=== 5 || viewVal === 6) navToProfile(viewVal);

  }, [viewVal]);

  /**
   * Back reopens the profile menu this page was opened from, rather than the
   * home tab or Personal Information.
   *
   * Providers reach the same component through the /help-support route, where it
   * is rendered without either prop — so calling one unguarded threw, and their
   * back button did nothing at all.
   */
  const handleBack = () => {
    if (props.openProfileMenu) props.openProfileMenu();
    else navigate('/doctor-profile');
  };

  const containerStyle = {
    maxWidth: '1024px',
    margin: '0 auto',
    color: 'var(--foreground)'
  };

  const cardStyle = {
    backgroundColor: 'var(--card)',
    border: '1px solid var(--border)',
    borderRadius: '8px',
    padding: '24px',
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr', // Roughly 66% / 33% split
    gap: '24px',
    marginTop: '32px',
  };

  return (
    <div style={{ display: 'flex', flexDirection: "column", width: "100%" }} >
      <div style={containerStyle}>
          {/* --- Header Section --- */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
            <button
              style={{
                padding: '8px',
                borderRadius: '8px',
                border: '1px solid var(--border)',
                backgroundColor: 'var(--card)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s'
              }}
              onClick={handleBack}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.05)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m12 19-7-7 7-7"/><path d="M19 12H5"/>
              </svg>
            </button>

            <div>
              <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600 }}>Help & Support</h2>
              <p style={{ margin: '4px 0 0 0', opacity: 0.7, fontSize: '1rem', color: 'var(--muted-foreground)' }}>Get in touch with our support team</p>
            </div>
          </div>

          {/* --- Main Content Grid --- */}
          <div style={gridStyle}>

            {/* Left Column (2/3 width) */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

              {/* Contact Methods Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>

                {/* Call Us card removed: 1-855-555-5555 was a placeholder, and a number
                    that does not answer is worse than none on a health support page. */}

                {/* FAQ sits ahead of Email Us: most questions are answered there
                    already, and it costs nobody a support round-trip. */}
                <div style={{ ...cardStyle, display: 'flex', flexDirection: 'column' }}>
                  <div style={{ width: '48px', height: '48px', borderRadius: '8px', backgroundColor: 'var(--youro-mint)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--youro-sage)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/>
                    </svg>
                  </div>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '1rem', fontWeight: 600 }}>Need Quick Answers?</h4>
                  <p style={{ margin: '0 0 16px 0', fontSize: '0.875rem', opacity: 0.7 }}>Check out our FAQ section for instant answers to common questions.</p>
                  <button
                    onClick={() => navigate('/faq')}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--accent)';
                      e.currentTarget.style.transform = 'scale(1.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#faf7f2';
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                    style={{
                      width: '100%',
                      height: '36px',
                      marginTop: 'auto',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      border: '1px solid var(--youro-sage)',
                      backgroundColor: '#faf7f2',
                      color: 'var(--youro-sage)',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      cursor: 'pointer',
                      transition: 'all 0.3s ease-in-out'
                    }}>
                    View FAQ
                  </button>
                </div>

                {/* Email Us Card */}
                <a href="mailto:yourohealth1@gmail.com" style={{ textDecoration: 'none', color: 'inherit', ...cardStyle, display: 'block', cursor: 'pointer', transition: 'transform 0.3s ease-in-out' }} onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.05)'; }} onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }} >
                  <div style={{ width: '48px', height: '48px', borderRadius: '8px', backgroundColor: 'var(--youro-mint)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--youro-sage)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7"/><rect x="2" y="4" width="20" height="16" rx="2"/>
                    </svg>
                  </div>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '1rem', fontWeight: 600 }}>Email Us</h4>
                  <p style={{ margin: '0 0 8px 0', fontSize: '1.125rem', fontWeight: 500 }}>yourohealth1@gmail.com</p>
                  <p style={{ margin: 0, fontSize: '0.875rem', opacity: 0.6 }}>Response within 24 hours</p>
                </a>
              </div>

              {/* Send Message Form */}
              <div style={cardStyle}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--youro-sage)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 17a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 2 21.286V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2z"/>
                  </svg>
                  <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>Send Us a Message</h3>
                </div>

                {isSent ? (
                  <div style={{ padding: '48px 0', textAlign: 'center', opacity: 1, animation: 'fadeIn 0.5s ease-out' }}>
                    <div style={{ width: '64px', height: '64px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px auto', backgroundColor: 'var(--youro-mint)' }}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--youro-sage)' }}>
                        <path d="M22 17a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 2 21.286V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2z"></path>
                      </svg>
                    </div>
                    <h3 style={{ margin: '0 0 8px 0', fontSize: '1.25rem', fontWeight: 600 }}>Message Sent!</h3>
                    <p style={{ margin: 0, opacity: 0.7 }}>We've received your message and will respond within 24 hours.</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', animation: 'fadeIn 0.5s ease-out' }}>
                    <div>
                      <label htmlFor="support-subject" style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '8px' }}>Subject</label>
                      <input
                        id="support-subject"
                        type="text"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        placeholder="What can we help you with?"
                        onFocus={(e) => e.target.style.boxShadow = '0 0 0 3px rgba(143, 168, 122, 0.6)'}
                        onBlur={(e) => e.target.style.boxShadow = 'none'}
                        style={{
                          width: '100%',
                          height: '36px',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          border: '1px solid var(--input)',
                          backgroundColor: 'var(--youro-sand)',
                          fontSize: '0.875rem',
                          boxSizing: 'border-box',
                          transition: 'box-shadow 0.2s ease-in-out'
                        }}
                      />
                    </div>

                    <div>
                      <label htmlFor="support-message" style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '8px' }}>Message</label>
                      <textarea
                        id="support-message"
                        rows="6"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Describe your question or issue in detail..."
                        onFocus={(e) => e.target.style.boxShadow = '0 0 0 3px rgba(143, 168, 122, 0.6)'}
                        onBlur={(e) => e.target.style.boxShadow = 'none'}
                        style={{
                          width: '100%',
                          minHeight: '80px',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          border: '1px solid var(--input)',
                          backgroundColor: 'var(--youro-sand)',
                          fontSize: '0.875rem',
                          resize: 'none',
                          boxSizing: 'border-box',
                          fontFamily: 'inherit'
                        }}
                      />
                    </div>

                    <button
                      disabled={!isValid || sending}
                      aria-busy={sending}
                      onClick={handleSendMessage}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                      style={{
                        width: '100%',
                        height: '36px',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        border: 'none',
                        backgroundColor: isValid ? 'var(--youro-coral)' : 'var(--youro-stone)',
                        color: 'white',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        cursor: isValid ? 'pointer' : 'not-allowed',
                        opacity: 0.8,
                        transition: 'all 0.3s ease-in-out'
                      }}
                    >
                      Send Message
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Right Sidebar (1/3 width) */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

              {/* Support Hours Card */}
              <div style={{ ...cardStyle, backgroundColor: 'var(--youro-mint)', borderColor: 'var(--youro-sage)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--youro-sage)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 6v6l4 2"/><circle cx="12" cy="12" r="10"/>
                  </svg>
                  <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>Support Hours</h4>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
                    <span>Monday - Friday</span>
                    <span>8am - 8pm EST</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
                    <span>Saturday</span>
                    <span>10am - 6pm EST</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
                    <span>Sunday</span>
                    <span>10am - 6pm EST</span>
                  </div>
                </div>
              </div>

              {/* Emergency Card — moved up into the slot the FAQ card vacated. */}
              <div style={{
                padding: '24px',
                borderRadius: '8px',
                backgroundColor: 'var(--youro-card)',
                border: '2px solid var(--youro-charcoal)'
              }}>
                <h4 style={{ margin: '0 0 8px 0', fontSize: '1rem', fontWeight: 700, color: 'var(--youro-charcoal)' }}>
                  Medical Emergency?
                </h4>
                <p style={{ margin: '0 0 12px 0', fontSize: '0.875rem', opacity: 0.9 }}>
                  If you're experiencing a medical emergency, call 911 immediately or visit your nearest emergency room.
                </p>
                <p style={{ margin: 0, fontSize: '0.875rem', opacity: 0.9 }}>
                  Youro support is for non-urgent questions only.
                </p>
              </div>

            </div>
          </div>
      </div>
    </div>
  );
};

export default HelpSupportView;